import React, { FC, FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { EditableSection, IEditableSectionRenderProps } from "@/components/EditableSection";
import { Header } from "@/components/Header";
import { initialWorkstyles } from "@/data/consultant-profiles";
import { Flex, Section } from "@/eworkitComponents";
import { IConsultantProfileSectionProps } from "@/views/components/ConsultantProfile/Sections/types";
import { EmptyWorkStyles } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/EmptyWorkStyles";
import { SingleWorkStyle } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/SingleWorkstyle";
import { WorkstylesEdit } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/WorkstylesForm";
import { WORKSTYLES_ICON_MAP } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/WorkstylesSectionEdit";

interface IWorkStylesSectionProps extends IEditableWorkStylesSectionProps, IEditableSectionRenderProps {
  trackClick?: (sectionName: string) => void;
  handleEmptyEditClick: (callback: () => void) => () => void;
}

export const WorkStylesSection: FunctionComponent<IWorkStylesSectionProps> = ({
  editableProps,
  record,
  handleEmptyEditClick,
  onUpdate,
}) => {
  const { t } = useTranslation("session");
  const emptyWorkstyles =
    !record.workStyles ||
    (record.workStyles.reduce((acc, curr) => acc + curr.score, 0) <= 0 && !editableProps.editMode);

  return (
    <Section grow={1} direction="column" innerMargin="md" testName="workStyles">
      <Header size="sm" color="black" weight="bold">
        {t("CONSULTANT_PROFILES.DETAIL.WORK_STYLES.SECTION_HEADER")}
      </Header>
      {emptyWorkstyles && <EmptyWorkStyles setEditMode={handleEmptyEditClick(editableProps.setEditMode)} />}
      {!emptyWorkstyles &&
        (editableProps.editMode ? (
          <WorkstylesEdit
            source={record}
            initialValues={{ workStyles: record.workStyles?.length ? record.workStyles : initialWorkstyles }}
            onUpdate={onUpdate}
            onCancel={editableProps.handleCancelClick}
            editableProps={editableProps}
          />
        ) : (
          <Flex direction="column" innerMargin="sm">
            {record.workStyles
              ?.sort((a, b) => b.score - a.score)
              .map(w => (
                <SingleWorkStyle key={w.type} WorkstyleIcon={WORKSTYLES_ICON_MAP[w.type]} {...w} />
              ))}
          </Flex>
        ))}
    </Section>
  );
};

interface IEditableWorkStylesSectionProps extends IConsultantProfileSectionProps {
  trackClick?: (sectionName: string) => void;
}

export const EditableWorkStylesSection: FC<IEditableWorkStylesSectionProps> = ({ record, onUpdate, trackClick }) => {
  const [isEmptyEditMode, setIsEmptyEditMode] = useState(false);
  const nonEmptyWorkstyles = !!record.workStyles && record.workStyles.reduce((acc, curr) => acc + curr.score, 0) > 0;

  const handleEmptyEditClick = useCallback(
    (callback: () => void) => () => {
      setIsEmptyEditMode(true);
      callback();
    },
    [],
  );
  return (
    <EditableSection
      hasCancelButton={nonEmptyWorkstyles || isEmptyEditMode}
      editable={nonEmptyWorkstyles || isEmptyEditMode}
      onEditClick={() => trackClick?.("Workstyles")}
      onCancelClick={() => setIsEmptyEditMode(false)}>
      {editableProps => (
        <WorkStylesSection
          handleEmptyEditClick={handleEmptyEditClick}
          editableProps={editableProps}
          record={record}
          onUpdate={onUpdate}
        />
      )}
    </EditableSection>
  );
};
